import React, { useState, useEffect } from "react";
import { FaAngleRight } from "react-icons/fa6";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { setCurrentTab } from "../../../redux/fees/feeSlice";
import GlobalSettings from "./components/GlobalSettings";
import VendorSpecificFees from "./components/VendorSpecificFees";
import FeeHistory from "./components/FeeHistory";
import { mockGlobalFeeSettings } from "./mockData";
import { formatCurrency } from "./mockData";

const Index = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentTab, globalSettings, vendorSpecificFees, feeHistory, loading } = useAppSelector((state) => state.fees);

  const { restaurantGlobalFees } = useAppSelector((state) => state.fees);
  
  // Dashboard metrics from real API data
  const dashboardMetrics = {
    standardCommissionRate: restaurantGlobalFees?.commissionRate || globalSettings?.defaultCommissionRate || mockGlobalFeeSettings.defaultCommissionRate,
    deliveryRate: restaurantGlobalFees?.deliveryRate || globalSettings?.defaultBaseFee || mockGlobalFeeSettings.defaultBaseFee,
    serviceCharge: restaurantGlobalFees?.serviceCharge || globalSettings?.defaultServiceChargeValue || mockGlobalFeeSettings.defaultServiceChargeValue,
    customRestaurants: restaurantGlobalFees?.restaurantOverrides?.length || 0,
  };

  const handleTabChange = (tab: 'global' | 'vendor' | 'history') => {
    dispatch(setCurrentTab(tab));
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'global':
        return <GlobalSettings />;
      case 'vendor':
        return <VendorSpecificFees />;
      case 'history':
        return <FeeHistory />;
      default:
        return <GlobalSettings />;
    }
  };

  return (
    <section className="font-mont">
      <div className="flex items-start gap-4">
        <button
          className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
          onClick={() => navigate(-1)}
        >
          <MdOutlineKeyboardBackspace className="w-6 h-6" />
        </button>
        <article>
          <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
            <span className="font-semibold">Restaurant</span> <FaAngleRight />
            <span className="font-semibold">Fee Management</span>
          </h1>
          <p className="text-sm">
            Manage fee structures, commission rates, and payment configurations for restaurants.
          </p>
        </article>
      </div>

      {/* Dashboard Metrics */}
      <section className="bg-white px-4 py-7 my-5 flex gap-3 justify-between">
        <div className="w-[269px] border rounded-md p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm">Standard Commission Rate</h3>
            <div className="p-3 rounded-full border">
              <span className="text-lg">%</span>
            </div>
          </div>
          <p className="text-lg font-medium">{dashboardMetrics.standardCommissionRate}%</p>
          <p className="text-xs text-gray-500">Current rate</p>
        </div>

        <div className="w-[269px] border rounded-md p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm">Delivery Rate</h3>
            <div className="p-3 rounded-full border">
              <span className="text-lg">₦</span>
            </div>
          </div>
          <p className="text-lg font-medium">{formatCurrency(dashboardMetrics.deliveryRate)}</p>
          <p className="text-xs text-gray-500">Base delivery fee</p>
        </div>

        <div className="w-[269px] border rounded-md p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm">Service Charge</h3>
            <div className="p-3 rounded-full border">
              <span className="text-lg">%</span>
            </div>
          </div>
          <p className="text-lg font-medium">{dashboardMetrics.serviceCharge}%</p>
          <p className="text-xs text-gray-500">Per order</p>
        </div>

        <div className="w-[269px] border rounded-md p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm">Custom Restaurants</h3>
            <div className="p-3 rounded-full border">
              <span className="text-lg">🏪</span>
            </div>
          </div>
          <p className="text-lg font-medium">{dashboardMetrics.customRestaurants}</p>
          <p className="text-xs text-gray-500">With custom fees</p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-white rounded-t-md border-b">
        <div className="flex space-x-8 px-6 py-4">
          <button
            onClick={() => handleTabChange('global')}
            className={`pb-2 px-1 font-medium text-sm border-b-2 transition-colors duration-200 ${
              currentTab === 'global'
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Global Settings
          </button>
          <button
            onClick={() => handleTabChange('vendor')}
            className={`pb-2 px-1 font-medium text-sm border-b-2 transition-colors duration-200 ${
              currentTab === 'vendor'
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Vendor Specific Fees
          </button>
          <button
            onClick={() => handleTabChange('history')}
            className={`pb-2 px-1 font-medium text-sm border-b-2 transition-colors duration-200 ${
              currentTab === 'history'
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fee History
          </button>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-gray-50 min-h-screen p-6">
        {renderTabContent()}
      </section>
    </section>
  );
};

export default Index;
